
import { useMemo, useCallback, useEffect } from 'react';
import { useAdminName } from '@/hooks/admin/useAdminName';
import { useAdminInitialLoad } from '@/hooks/admin/useAdminInitialLoad';
import { useAdminLoading } from '@/hooks/admin/useAdminLoading';
import { useAdminReconnection } from '@/hooks/admin/useAdminReconnection';
import { useToast } from '@/hooks/use-toast';

// Import our created smaller hooks
import {
  useAdminPageMode,
  useAdminPageAttendees,
  useAdminPageActivity,
  useAdminPageAttendance,
  useAdminPageSync,
  useAdminPageUI,
  useAdminPageAuth
} from '@/hooks/admin/useAdminPageHooks';

/**
 * Main hook for the Admin page
 * Combines all admin functionality by composing smaller hooks
 * with enhanced database reliability
 */
export const useAdminPage = () => {
  const { toast } = useToast();
  
  // Get admin name using dedicated hook
  const { adminName } = useAdminName();
  
  // Use the mode toggle hook - now always production
  const {
    isProduction,
    connectionStatus,
    checkConnection
  } = useAdminPageMode();
  
  // Use the UI state hook
  const {
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab
  } = useAdminPageUI();
  
  // Use the attendees data hook - with better refresh handling
  const {
    attendees,
    refreshAttendees,
    silentlyUpdateAttendee,
    lastRefreshTime,
    generateDemoData,
    resetData,
    isLoading: attendeesLoading,
    usingSupabase: attendeesUsingSupabase,
    totalRegistered,
    totalAttended,
    totalContribution,
    attendancePercentage,
    tShirtStats,
    hasEmptyAttendees,
    updateAttendee
  } = useAdminPageAttendees();
  
  // Use the admin activity hook
  const {
    adminActions,
    adminState,
    recordAction,
    removeActiveAdmin,
    refreshAdminData,
    isLoading: activityLoading,
    isActionThrottled,
    usingSupabase: activityUsingSupabase
  } = useAdminPageActivity();
  
  // Memoize the usingSupabase value
  const usingSupabase = useMemo(
    () => attendeesUsingSupabase && activityUsingSupabase,
    [attendeesUsingSupabase, activityUsingSupabase]
  );
  
  // Use dedicated hook for loading state
  const { isLoading } = useAdminLoading(
    attendeesLoading,
    activityLoading,
    false, // isChangingMode is always false
    connectionStatus
  );
  
  // Use dedicated hook for reconnection handling
  const { handleRetryConnection } = useAdminReconnection(
    checkConnection,
    refreshAttendees,
    refreshAdminData,
    toast
  );
  
  // Handle initial data loading with dedicated hook
  useAdminInitialLoad(
    refreshAdminData,
    refreshAttendees,
    recordAction,
    adminName,
    isLoading
  );
  
  // Use the attendance management hook - now with more reliable database operations
  const {
    selectedAttendee,
    isDialogOpen,
    setIsDialogOpen,
    handleQrScan,
    handleCodeSubmit,
    confirmAttendance,
    simulateQrScan,
    isConfirming
  } = useAdminPageAttendance(attendees, recordAction, adminName, refreshAttendees);
  
  // Use the data synchronization hook
  const {
    lastSyncTime,
    handleManualSync,
    handleResetData
  } = useAdminPageSync(
    adminState, 
    refreshAttendees, 
    refreshAdminData, 
    resetData, 
    recordAction, 
    adminName, 
    usingSupabase
  );
  
  // Use the auth hook
  const { handleLogout } = useAdminPageAuth(
    recordAction,
    removeActiveAdmin,
    adminName
  );
  
  // Enhanced direct update of attendee data
  const forceUpdateAttendee = useCallback(async (code: string, attended: boolean = true) => {
    if (!code) return null;
    
    console.log(`Force updating attendee with code ${code}, setting attended=${attended}`);
    
    try {
      // Use direct database update
      const timestamp = new Date().toISOString();
      const result = await updateAttendee(code, { 
        attended, 
        checkedInAt: attended ? timestamp : null 
      });
      
      if (result) {
        console.log("Force update successful, refreshing data");
        
        // Dispatch a global attendance update event
        window.dispatchEvent(new CustomEvent('global-attendance-update', {
          detail: {
            code: code,
            timestamp: timestamp,
            refreshRequired: true,
            highPriority: true
          }
        }));
        
        // Refresh attendees data
        refreshAttendees();
        return result;
      }
      
      return null;
    } catch (error) {
      console.error("Error in force update:", error);
      return null;
    }
  }, [updateAttendee, refreshAttendees]);
  
  // Enhanced event listening for database updates
  useEffect(() => {
    const handleDatabaseUpdate = (event: CustomEvent) => {
      if (!event.detail) return;
      
      console.log("Database update detected:", event.type, event.detail);
      
      // Check if this is a high-priority update that requires immediate refresh
      const isHighPriority = event.detail.highPriority === true;
      const requiresRefresh = event.detail.refreshRequired === true || 
                             event.detail.forceRefresh === true;
      
      // If we have enough information, trigger a refresh based on priority
      if ((event.detail.attendeeCode || event.detail.code) && 
          (isHighPriority || requiresRefresh || event.type === 'global-attendance-update')) {
        console.log(`High-priority database update for attendee ${event.detail.attendeeCode || event.detail.code}, refreshing data immediately`);
        
        // For high-priority updates, refresh immediately
        setTimeout(() => refreshAttendees(), 300);
        
        // For certain critical events, schedule a follow-up refresh
        if (event.type === 'manual-code-processed' || 
            event.type === 'global-attendance-update' || 
            event.detail.wasManualEntry === true) {
          console.log('Scheduling follow-up refresh for manual entry');
          setTimeout(() => refreshAttendees(), 1500);
        }
      } else if (event.detail.attendeeCode || event.detail.code) {
        // For regular updates, use a longer delay to batch potential updates
        console.log(`Standard database update for attendee ${event.detail.attendeeCode || event.detail.code}, scheduling refresh`);
        setTimeout(() => refreshAttendees(), 1000);
      }
    };
    
    // Listen for all types of database update events
    const eventTypes = [
      'database-update-success',
      'attendance-confirmed',
      'attendance-direct-confirmed',
      'global-attendance-update',
      'manual-code-processed',
      'attendance-ui-update'
    ];
    
    eventTypes.forEach(eventType => {
      window.addEventListener(eventType, handleDatabaseUpdate as EventListener);
    });
    
    return () => {
      eventTypes.forEach(eventType => {
        window.removeEventListener(eventType, handleDatabaseUpdate as EventListener);
      });
    };
  }, [refreshAttendees]);
  
  // Return all the necessary properties and functions
  return {
    searchQuery,
    setSearchQuery,
    selectedAttendee,
    isDialogOpen,
    setIsDialogOpen,
    activeTab,
    setActiveTab,
    lastSyncTime,
    lastRefreshTime,
    attendees,
    adminActions,
    adminState,
    adminName,
    totalRegistered,
    totalAttended,
    totalContribution,
    attendancePercentage,
    tShirtStats,
    isLoading,
    handleQrScan,
    handleCodeSubmit,
    confirmAttendance,
    simulateQrScan,
    handleLogout,
    handleManualSync,
    handleResetData,
    usingSupabase,
    isConfirming,
    isActionThrottled,
    isProduction,
    // Connection status
    connectionStatus,
    handleRetryConnection,
    // Add these to make them accessible
    generateDemoData,
    hasEmptyAttendees,
    // Make sure to include refreshAttendees 
    refreshAttendees,
    // Add the silent update function
    silentlyUpdateAttendee,
    // Add the force update function
    forceUpdateAttendee,
    // Explicitly expose updateAttendee
    updateAttendee
  };
};

// No nested export here - the hook is already exported at the top level
